// CB:BEGIN realtor-onboarding
import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import ContentLimiter from '../components/layout/ContentLimiter';
import { useDevice } from '../hooks/useDevice';
import { CheckCircle2, ArrowRight, ChevronLeft } from 'lucide-react';

const BRAND_GREEN = '#123c3b';
const TOTAL_STEPS = 2;
const REALTOR_AGREEMENT_VERSION = '1';

interface RealtorOnboardingProps {
  onNavigate: (screen: string) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1.5px solid #e2e8f0',
  borderRadius: 10,
  fontSize: 15,
  color: '#0f172a',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const RealtorOnboarding: React.FC<RealtorOnboardingProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const authFetch = useAuthFetch();
  const { isMobileLayout } = useDevice();

  const [step, setStep] = useState(0); // 0=loading, 1=profile, 2=agreement, 3=done
  const [brokerageName, setBrokerageName] = useState('');
  const [phone, setPhone] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementSubmitting, setAgreementSubmitting] = useState(false);
  const [agreementError, setAgreementError] = useState('');

  useEffect(() => {
    const check = async () => {
      try {
        const res = await authFetch(getApiUrl('/api/realtor/me'));
        if (res.ok) {
          const data = await res.json();
          if (data.agreementAccepted && data.profile?.brokerageName) {
            localStorage.setItem('cb.realtor.onboardingComplete', 'true');
            onNavigate('realtor-dashboard');
            return;
          }
          if (data.profile?.brokerageName) setBrokerageName(data.profile.brokerageName);
          if (data.profile?.phone) setPhone(data.profile.phone);
          if (data.profile?.licenseNumber) setLicenseNumber(data.profile.licenseNumber);
        }
      } catch (e) {
        console.error('[RealtorOnboarding] profile check failed', e);
      }

      // Apply pre-signup data collected during the landing page signup form
      try {
        const raw = sessionStorage.getItem('cb.realtor.presignup');
        if (raw) {
          sessionStorage.removeItem('cb.realtor.presignup');
          const pre = JSON.parse(raw) as { brokerageName?: string; phone?: string | null; licenseNumber?: string | null };
          if (pre.brokerageName) setBrokerageName(pre.brokerageName);
          if (pre.phone) setPhone(pre.phone);
          if (pre.licenseNumber) setLicenseNumber(pre.licenseNumber);
        }
      } catch {}

      setStep(1);
    };
    check();
  }, []);

  const handleProfileSubmit = async () => {
    if (!brokerageName.trim()) {
      setProfileError('Brokerage or company name is required.');
      return;
    }
    setProfileError('');
    setProfileSubmitting(true);
    try {
      const res = await authFetch(getApiUrl('/api/realtor/profile'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brokerageName: brokerageName.trim(),
          phone: phone.trim() || null,
          licenseNumber: licenseNumber.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setProfileError((err as any).error || 'Failed to save profile. Please try again.');
        return;
      }
      setStep(2);
    } catch {
      setProfileError('Network error. Please try again.');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handleAgreementSubmit = async () => {
    if (!agreementChecked) {
      setAgreementError('Please accept the agreement to continue.');
      return;
    }
    setAgreementError('');
    setAgreementSubmitting(true);
    try {
      const res = await authFetch(getApiUrl('/api/realtor/agreement/accept'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: REALTOR_AGREEMENT_VERSION }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setAgreementError((err as any).error || 'Failed to accept agreement. Please try again.');
        return;
      }
      localStorage.setItem('cb.realtor.onboardingComplete', 'true');
      setStep(3);
      setTimeout(() => onNavigate('realtor-dashboard'), 1500);
    } catch {
      setAgreementError('Network error. Please try again.');
    } finally {
      setAgreementSubmitting(false);
    }
  };

  if (step === 0) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <span style={{ color: '#64748b', fontSize: 16 }}>Loading your account…</span>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <CheckCircle2 size={64} style={{ color: BRAND_GREEN, marginBottom: 24 }} />
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>You're all set!</h2>
          <p style={{ color: '#64748b', fontSize: 16 }}>Taking you to your Realtor Dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ background: BRAND_GREEN, padding: isMobileLayout ? '16px 20px' : '20px 40px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <span
          style={{ fontSize: 20, fontWeight: 700, color: '#fff', cursor: 'pointer' }}
          onClick={() => onNavigate('landing')}
        >
          CoreBids
        </span>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>Realtor Setup</span>
      </div>

      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '16px 20px' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Step {step} of {TOTAL_STEPS}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: BRAND_GREEN }}>
                {step === 1 ? 'Your Profile' : 'Platform Agreement'}
              </span>
            </div>
            <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99 }}>
              <div style={{
                height: 6,
                borderRadius: 99,
                background: BRAND_GREEN,
                width: `${(step / TOTAL_STEPS) * 100}%`,
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        </ContentLimiter>
      </div>

      <ContentLimiter>
        <div style={{ maxWidth: 560, margin: '0 auto', padding: isMobileLayout ? '32px 20px' : '48px 20px' }}>

          {step === 1 && (
            <div>
              <h1 style={{ fontSize: isMobileLayout ? 26 : 32, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                Set up your profile
              </h1>
              <p style={{ fontSize: 16, color: '#64748b', marginBottom: 32, lineHeight: 1.6 }}>
                Your brokerage name appears on client PDFs and co-branded portal links.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {user && (
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Your Name</label>
                    <div style={{ ...inputStyle, background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed' }}>
                      {user.fullName || user.emailAddresses?.[0]?.emailAddress || '—'}
                    </div>
                    <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>From your account — update in profile settings.</p>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Brokerage / Company Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    style={inputStyle}
                    placeholder="e.g. Keller Williams — Jane Smith Team"
                    value={brokerageName}
                    onChange={e => setBrokerageName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleProfileSubmit()}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Phone Number</label>
                  <input
                    style={inputStyle}
                    placeholder="(555) 000-0000"
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Real Estate License #{' '}
                    <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                  </label>
                  <input
                    style={inputStyle}
                    placeholder="State license number"
                    value={licenseNumber}
                    onChange={e => setLicenseNumber(e.target.value)}
                  />
                </div>

                {profileError && (
                  <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14 }}>
                    {profileError}
                  </div>
                )}

                <button
                  onClick={handleProfileSubmit}
                  disabled={profileSubmitting}
                  style={{
                    padding: '14px 28px',
                    background: BRAND_GREEN,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: profileSubmitting ? 'not-allowed' : 'pointer',
                    opacity: profileSubmitting ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {profileSubmitting ? 'Saving…' : <><span>Continue</span> <ArrowRight size={18} /></>}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <button
                onClick={() => setStep(1)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 24, fontSize: 14, padding: 0 }}
              >
                <ChevronLeft size={16} /> Back
              </button>

              <h1 style={{ fontSize: isMobileLayout ? 26 : 32, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                Platform Agreement
              </h1>
              <p style={{ fontSize: 16, color: '#64748b', marginBottom: 24, lineHeight: 1.6 }}>
                Review and accept the CoreBids Realtor Platform Agreement to activate your account.
              </p>

              <div style={{
                background: '#fff',
                border: '1.5px solid #e2e8f0',
                borderRadius: 12,
                padding: isMobileLayout ? 20 : 28,
                maxHeight: 320,
                overflowY: 'auto',
                fontSize: 14,
                color: '#374151',
                lineHeight: 1.7,
                marginBottom: 24,
              }}>
                <p style={{ fontWeight: 700, marginBottom: 12 }}>CoreBids Realtor Platform Agreement — Version 1</p>
                <p style={{ marginBottom: 12 }}>By accepting this agreement, you ("Realtor") agree to use the CoreBids platform in accordance with these terms:</p>
                <ol style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 10, margin: 0 }}>
                  <li><strong>Service Use.</strong> You may use CoreBids to request roofing quotes on behalf of your clients, download client-ready PDFs, and share co-branded Client Portal links.</li>
                  <li><strong>Accurate Information.</strong> You will provide accurate property addresses and represent that you have authorization to request quotes for the listed property.</li>
                  <li><strong>No Fee to Realtors.</strong> Realtor accounts and quote requests are provided at no charge. Fees are collected from contractors and homeowners through the project payment flow.</li>
                  <li><strong>Referral Attribution.</strong> When a client accepts a bid through your portal link, the resulting project will be attributed to your account for tracking purposes.</li>
                  <li><strong>Referral Incentives.</strong> CoreBids may offer referral incentives at its discretion. Incentive eligibility, amounts, and payout procedures are governed by CoreBids' current incentive policy.</li>
                  <li><strong>Prohibited Uses.</strong> You may not use CoreBids to submit fraudulent addresses, scrape contractor data, or circumvent the platform's payment or bid processes.</li>
                  <li><strong>Platform Rules.</strong> CoreBids reserves the right to suspend or terminate accounts that violate these terms or engage in abuse.</li>
                  <li><strong>Governing Law.</strong> This agreement is governed by applicable law. Disputes will be resolved through binding arbitration or small claims court.</li>
                </ol>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', fontSize: 15, color: '#0f172a', lineHeight: 1.5 }}>
                  <input
                    type="checkbox"
                    checked={agreementChecked}
                    onChange={e => setAgreementChecked(e.target.checked)}
                    style={{ marginTop: 3, width: 18, height: 18, cursor: 'pointer', accentColor: BRAND_GREEN }}
                  />
                  I have read and agree to the CoreBids Realtor Platform Agreement (Version 1).
                </label>

                {agreementError && (
                  <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14 }}>
                    {agreementError}
                  </div>
                )}

                <button
                  onClick={handleAgreementSubmit}
                  disabled={agreementSubmitting || !agreementChecked}
                  style={{
                    padding: '14px 28px',
                    background: BRAND_GREEN,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: (agreementSubmitting || !agreementChecked) ? 'not-allowed' : 'pointer',
                    opacity: (agreementSubmitting || !agreementChecked) ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {agreementSubmitting ? 'Activating…' : <><span>Activate My Account</span> <ArrowRight size={18} /></>}
                </button>
              </div>
            </div>
          )}

        </div>
      </ContentLimiter>
    </div>
  );
};

export default RealtorOnboarding;
// CB:END realtor-onboarding
